<template>
  <div class="campaign-info-card">
    <div class="card-header card-panel">
      <div class="title-row">
        <h2 class="campaign-title">{{ item.title || '—' }}</h2>
        <Status :type="statusType(item?.status)" :text="item.status" />
      </div>
      <div class="date-row">
        <svg class="date-icon" aria-hidden="true">
          <use xlink:href="#icon-rili-cu" />
        </svg>
        <span>{{ dateRange }}</span>
      </div>
    </div>

    <div class="card-body">
      <section class="info-section card-panel">
        <div class="section-title">
          <div class="section-img">
            <img src="../../../../assets/images/img/vector.png" alt=""  />
          </div>
         
          <span>Available Link Type & EPC</span>
        </div>
        <!-- v-if="item.attributionCpcEnabled" -->
        <div  class="metric-block">
          <div class="metric-block-head">
            <span class="metric-label">
              CPC(Attribution)
              <a-popover placement="top" :overlay-inner-style="{ maxWidth: '500px' }">
                <template #content>
                  Attribution Links may generate additional CPS commissions on top of EPC earnings.
                </template>
                <svg class="help-icon" aria-hidden="true">
                  <use xlink:href="#icon-wenhao-tianchong" />
                </svg>
              </a-popover>
            </span>
            <img src="../../../../assets/images/img/AT@2x.png" alt="" class="icon" />
          </div>
          <div class="metric-block-value">
            {{ item.attributionCpcEnabled ? currencySymbol + formatMoney(item.attributionCpcUnitPrice) : 'N/A' }}

          </div>
        </div>
        <!-- v-if="item.associateCpcEnabled" -->
        <div  class="metric-block">
          <div class="metric-block-head">
            <span class="metric-label">CPC (Associate)</span>
            <img src="../../../../assets/images/img/AS@2x.png" alt="" class="icon" />
          </div>
          <div class="metric-block-value">
            {{ item.associateCpcEnabled ? currencySymbol + formatMoney(item.associateCpcUnitPrice) : 'N/A' }}
          </div>
        </div>
      </section>

      <section class="info-section card-panel">
        <div class="section-title">
          <div class="section-img">
            <img src="../../../../assets/images/img/Limits.png" alt=""  />
          </div>
          <span>Budget & Earnings Limits</span>
        </div>

        <div class="budget-grid">
          <div class="metric-block">
            <div class="metric-label">Total Budget</div>
            <div class="metric-block-value">{{ currencySymbol }} {{ formatMoney(item.totalBudget) }}</div>
          </div>
          <div class="metric-block">
            <div class="metric-label">Remaining Budget</div>
            <div class="metric-block-value">{{ currencySymbol }} {{ formatMoney(item.budgetRemaining) }}</div>
          </div>
          <div class="metric-block">
            <div class="metric-label">Max Earnings (Total)</div>
            <div class="metric-block-value">{{ currencySymbol }} {{ formatMoney(item.publisherTotalCap) }}</div>
          </div>
          <div class="metric-block">
            <div class="metric-label">Max Earnings (Per Day)</div>
            <div class="metric-block-value">{{ currencySymbol }} {{ formatMoney(item.publisherDailyCap) }}</div>
          </div>
        </div>
      </section>

      <section class="info-section card-panel">
        <div class="section-title">
          <div class="section-img">
            <img src="../../../../assets/images/img/Performance.png" alt=""  />
          </div>
          <span>Performance</span>
        </div>

        <div class="performance-top">
          <div class="metric-block performance-card">
            <div class="performance-card-head">
              <span class="metric-label">Your Clicks</span>
              <span class="performance-icon performance-icon-click">
                <svg aria-hidden="true">
                  <use xlink:href="#icon-jian-dianji" />
                </svg>
              </span>
            </div>
            <div class="metric-block-value">{{ item.yourClicks ?? 0 }}</div>
          </div>
          <div class="metric-block performance-card">
            <div class="performance-card-head">
              <span class="metric-label">Your Earnings</span>
              <span class="performance-icon performance-icon-earn">
                <svg aria-hidden="true">
                  <use xlink:href="#icon-dollar-circle" />
                </svg>
              </span>
            </div>
            <div class="metric-block-value">{{ currencySymbol }} {{ formatMoney(item.yourEarnings) }}</div>
          </div>
        </div>

        <div class="fulfilled-block">
          <div class="metric-label">Percent Fulfilled</div>
          <div class="metric-block-value" style="margin-top: 8px;">{{ percentFulfilled }}%</div>
          <div class="progress-track">
            <div class="progress-fill" :style="{ width: `${percentFulfilled}%` }" />
          </div>
          <div class="progress-scale">
            <span>0%</span>
            <span>100%</span>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import moment from 'moment'
import Status from '~/components/Status.vue'
import { CURRENCY_DATA_MAPPER, COUNTRY_TIME_TZ, COUNTRY_SITE_CODE_MAP } from '~/contants/common'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
})

const timezone = computed(
  () => COUNTRY_TIME_TZ[COUNTRY_SITE_CODE_MAP[props.item?.marketplace] || 'US'],
)

const currencySymbol = computed(
  () => CURRENCY_DATA_MAPPER[props.item?.currency] || '$',
)

const percentFulfilled = computed(() => {
  const value = Number(props.item?.percentFulfilled ?? 0)
  if (Number.isNaN(value)) return 0
  return Math.min(100, Math.max(0, value))
})

const formatMoney = (val) => {
  const num = Number(val ?? 0)
  if (Number.isNaN(num)) return '0'
  return Number.isInteger(num) ? String(num) : num.toFixed(2)
}

const statusType = (status) => {
  if (status === 'ACTIVE') return 'active'
  if (status === 'FINISHED') return 'finished'
  if(status === 'INCOMING') return 'delivering '
  return 'active'
}

const dateRange = computed(() => {
  if (props.item?.startDateTimestamp && props.item?.endDateTimestamp) {
    const start = moment(props.item.startDateTimestamp).tz(timezone.value).format('MMM D, YYYY')
    const end = moment(props.item.endDateTimestamp).tz(timezone.value).format('MMM D, YYYY')
    return `${start} – ${end}`
  }
  return '--'
})
</script>

<style scoped lang="less">
.icon{
  width: 20px;
  height: 20px;
}
.campaign-info-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card-panel {
  background: #fff;
  border: 1px solid #e8e5ed;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 6, 41, 0.04);
}

.card-header {
  padding: 32px;
}

.title-row {
  display: flex;
  align-items: center;
  // justify-content: space-between;
  gap: 12px;
}

.campaign-title {
  margin: 0;
  // flex: 1;
  // min-width: 0;
  font-family: Sora, sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.4px;
  color: rgba(15, 6, 41, 1);
  word-break: break-word;
}

.date-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  font-family: Sora, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: rgba(15, 6, 41, 1);
}

.date-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: rgba(15, 6, 41, 0.45);
}

.card-body {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}

.info-section {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 100%;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: Sora, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: rgba(15, 6, 41, 1);
}
.section-img{
  width: 32px;
  height: 32px;
  background-color: rgba(239, 236, 255, 1);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  img{
    width: 16px;
  }
}
.section-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: #7d64ff;
}

.metric-block {
  padding: 14px 16px;
  border-radius: 8px;
  background: rgba(250, 250, 251, 1);
}

.metric-block-head,
.performance-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.metric-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: Sora;
font-weight: 400;
font-style: Regular;
font-size: 12px;
leading-trim: NONE;
line-height: 100%;
letter-spacing: 0%;

  color: rgba(102, 112, 133, 1);
}

.help-icon {
  width: 14px;
  height: 14px;
  color: #acaab1;
  fill: #acaab1;
  cursor: pointer;
}

.link-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 4px;
  border-radius: 4px;
  font-family: Sora, sans-serif;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.2px;
}

.link-badge-at {
  color: #2aae67;
  background: rgba(42, 174, 103, 0.14);
}

.link-badge-as {
  color: #5089fa;
  background: rgba(80, 137, 250, 0.14);
}

.metric-block-value {
  margin-top: 12px;
  font-family: Sora;
font-weight: 700;
font-style: Bold;
font-size: 26px;
leading-trim: NONE;
line-height: 100%;
letter-spacing: 0%;
color: rgba(15, 6, 41, 1);

}

.budget-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.performance-top {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.performance-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;

  svg {
    width: 14px;
    height: 14px;
    fill: white;
  }
}

.performance-icon-click {
  color: #7d64ff;
  background: rgba(136, 136, 255, 1);
}

.performance-icon-earn {
  color: #f59b00;
  background: rgba(251, 139, 98, 1);
}

.fulfilled-block {
  // padding: 10px 12px;
  border-radius: 8px;
  // background: #f8f7fc;
}

.progress-track {
  margin-top: 8px;
  height: 8px;
  // border-radius: 999px;
  background: rgba(234, 236, 240, 1);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 999px;
  background: rgba(123, 77, 255, 1);
  transition: width 0.2s ease;
}

.progress-scale {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-family: Sora, sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: rgba(15, 6, 41, 0.4);
}

@media (max-width: 1100px) {
  .card-body {
    grid-template-columns: 1fr;
  }
}
</style>
